import clsx from 'clsx';
import { TFunction } from 'i18next';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneLink } from '@provider-directory/components/phone-number';
import {
  DecisionTreeContact,
  DecisionTreeLocationEntry,
  DecisionTreeNestedLocation,
} from '@provider-directory/types';
export const NoBreak = ({ children }: { children?: string }): ReactElement => {
  return <span className={'text-nowrap'}>{children}</span>;
};

export const Phone = ({
  phone,
  onClick,
  className = '',
  ariaLabel,
}: {
  phone: string;
  onClick: (href: string) => void;
  className?: string;
  ariaLabel?: string;
}): ReactElement => {
  return (
    <PhoneLink
      phone={phone}
      onClick={(e) => {
        e.preventDefault();
        window.open(e.currentTarget.href);
        onClick(e.currentTarget.href);
      }}
      className={clsx('text-primary-color underline', className)}
      aria-label={ariaLabel}
    >
      {({ formattedPhone }) => <NoBreak>{formattedPhone}</NoBreak>}
    </PhoneLink>
  );
};

const getLocationPrefix = (
  locationEntry: DecisionTreeNestedLocation,
  t: TFunction,
) =>
  locationEntry.city
    ? `${locationEntry.city} ${locationEntry.isMulti ? t('decisionTree.locationAtMulti') : t('decisionTree.locationAt')}`
    : t('decisionTree.locationAt');

interface ContactsListProps {
  contacts: DecisionTreeContact[];
  onClick: (href: string) => void;
}

const renderLocationEntry = (
  entry: DecisionTreeLocationEntry,
  t: TFunction,
): ReactElement => {
  if (typeof entry === 'string') {
    return <>{entry}</>;
  }

  const prefix = entry.city ? getLocationPrefix(entry, t) : '';
  const content = [prefix, entry.location].filter(Boolean).join(' ');

  return (
    <>
      {content}
      {entry.children && entry.children.length > 0 ? (
        <ul className={'list-disc pl-4 pt-2'}>
          {entry.children.map((child, idx) => (
            <li key={idx} className={'pb-1'}>
              {renderLocationEntry(child, t)}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
};

export const ContactsList = ({
  contacts,
  onClick,
}: ContactsListProps): ReactElement | null => {
  const { t } = useTranslation();

  if (contacts.length === 0) {
    return null;
  }

  return (
    <ul className={'list-disc pl-4 pt-2'}>
      {contacts.map((contact, idx) => {
        return (
          <li key={idx} className={'pb-1'}>
            {contact.name && <span className={'pr-1'}>{contact.name}:</span>}
            <Phone className={'mr-1'} onClick={onClick} phone={contact.phone} />
            {contact.description ? (
              <span className={'pl-1'}>{contact.description}</span>
            ) : null}
            {contact.locations.length > 0 ? (
              contact.locations.length === 1 ? (
                <span>for {renderLocationEntry(contact.locations[0], t)}</span>
              ) : (
                <ul className={'list-disc pl-4 pt-2'}>
                  {contact.locations.map((loc, i) => (
                    <li key={i} className={'pb-1'}>
                      {renderLocationEntry(loc, t)}
                    </li>
                  ))}
                </ul>
              )
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};
